(() => {
  const config = window.gillkart || {};
  const moneyFormat = config.moneyFormat || '{{amount}}';

  const formatMoney = (cents, format = moneyFormat) => {
    const number = (value, precision = 2, thousands = ',', decimal = '.') => {
      const fixed = (value / 100).toFixed(precision);
      const [whole, fraction] = fixed.split('.');
      return whole.replace(/(\d+)(?=(\d{3})+(?!\d))/g, `$1${thousands}`) + (fraction ? decimal + fraction : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, name) => {
      switch (name) {
        case 'amount_no_decimals': return number(cents, 0);
        case 'amount_with_comma_separator': return number(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return number(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return number(cents, 2, "'", '.');
        case 'amount_no_decimals_with_space_separator': return number(cents, 0, ' ');
        case 'amount_with_space_separator': return number(cents, 2, ' ', ',');
        default: return number(cents);
      }
    });
  };

  const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const getCart = async () => (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();

  const postJson = async (url, body) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || data.message || 'Could not update your bag.');
    return data;
  };

  const updateBadge = (count) => {
    const link = document.querySelector('.gk-actions__cart');
    if (!link) return;
    let badge = link.querySelector('[data-gk-cart-count]');
    if (!badge && count > 0) {
      badge = document.createElement('b');
      badge.setAttribute('data-gk-cart-count', '');
      link.appendChild(badge);
    }
    if (badge) {
      badge.textContent = count;
      badge.hidden = count === 0;
    }
  };

  /* ---------- Cart page ---------- */
  const pageRoot = () => document.querySelector('[data-gk-cart-root]');

  const refreshPage = async () => {
    const root = pageRoot();
    if (!root) return;
    const url = `${window.location.pathname}?section_id=${encodeURIComponent(root.dataset.sectionId)}`;
    const html = await (await fetch(url)).text();
    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-gk-cart-root]');
    if (next) root.replaceWith(next);
  };

  const changeLine = async (line, quantity, scope) => {
    const root = pageRoot();
    const error = root && root.querySelector('[data-gk-cart-error]');
    if (error) error.hidden = true;
    root && root.classList.add('is-loading');
    try {
      const cart = await postJson('/cart/change.js', { line: Number(line), quantity: Number(quantity) });
      updateBadge(cart.item_count);
      await refreshPage();
    } catch (e) {
      root && root.classList.remove('is-loading');
      const target = pageRoot() && pageRoot().querySelector('[data-gk-cart-error]');
      if (target) {
        target.textContent = e.message;
        target.hidden = false;
      }
      if (scope) scope.value = scope.defaultValue;
    }
  };

  document.addEventListener('click', (event) => {
    const qty = event.target.closest('[data-gk-qty]');
    if (qty && pageRoot() && pageRoot().contains(qty)) {
      const next = Number(qty.dataset.quantity) + Number(qty.dataset.gkQty);
      changeLine(qty.dataset.line, Math.max(next, 0));
      return;
    }
    const remove = event.target.closest('[data-gk-remove]');
    if (remove && pageRoot() && pageRoot().contains(remove)) changeLine(remove.dataset.line, 0);
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-gk-qty-input]');
    if (input && pageRoot() && pageRoot().contains(input)) {
      changeLine(input.dataset.line, Math.max(parseInt(input.value, 10) || 0, 0), input);
      return;
    }
    const note = event.target.closest('[data-gk-note]');
    if (note) postJson('/cart/update.js', { note: note.value }).catch(() => {});
  });

  document.addEventListener('submit', (event) => {
    if (event.target.id === 'gk-cart-form') {
      const submitter = event.submitter;
      if (!submitter || submitter.name !== 'checkout') event.preventDefault();
    }
  });

  /* ---------- Mini cart drawer ---------- */
  let drawer;
  let lastFocus;

  const lineHtml = (item, index) => {
    const options = item.product_has_only_default_variant ? '' : item.options_with_values.map((o) => `<li><span>${escapeHtml(o.name)}:</span> ${escapeHtml(o.value)}</li>`).join('');
    const image = item.image ? `<img src="${escapeHtml(item.image.replace(/(\.[a-z]+)(\?|$)/i, '_200x$1$2'))}" alt="" width="84" height="112" loading="lazy">` : '';
    const compare = item.original_price > item.final_price ? `<s>${formatMoney(item.original_price)}</s>` : '';
    return `
      <li class="gk-mini-item">
        <a class="gk-mini-item__image" href="${escapeHtml(item.url)}">${image}</a>
        <div class="gk-mini-item__body">
          <p class="gk-cart-item__vendor">${escapeHtml(item.vendor)}</p>
          <a class="gk-cart-item__title" href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
          ${options ? `<ul class="gk-cart-item__options">${options}</ul>` : ''}
          <div class="gk-cart-item__price"><b>${formatMoney(item.final_price)}</b>${compare}</div>
          <div class="gk-cart-item__controls">
            <div class="gk-qty">
              <button type="button" data-mini-qty="-1" data-line="${index + 1}" data-quantity="${item.quantity}" aria-label="Decrease quantity"${item.quantity <= 1 ? ' disabled' : ''}>&minus;</button>
              <span aria-live="polite">${item.quantity}</span>
              <button type="button" data-mini-qty="1" data-line="${index + 1}" data-quantity="${item.quantity}" aria-label="Increase quantity">+</button>
            </div>
            <button type="button" class="gk-cart-item__remove" data-mini-remove data-line="${index + 1}">Remove</button>
          </div>
        </div>
      </li>`;
  };

  const renderDrawer = (cart, message) => {
    if (!drawer) return;
    const body = drawer.querySelector('[data-mini-body]');
    const foot = drawer.querySelector('[data-mini-foot]');
    drawer.querySelector('[data-mini-count]').textContent = cart.item_count ? `(${cart.item_count})` : '';

    if (!cart.item_count) {
      body.innerHTML = `
        <div class="gk-mini-empty">
          <div class="gk-cart-empty__icon" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M4.2 8h15.6l-1.2 12.5H5.4L4.2 8Z"></path><path d="M8.5 9.5V7.8a3.5 3.5 0 0 1 7 0v1.7"></path></svg></div>
          <h3>Your bag is empty</h3>
          <p>Add something you love and it will show up here.</p>
          <a class="gk-button" href="${escapeHtml(config.collectionsUrl || '/collections')}">SHOP COLLECTIONS</a>
        </div>`;
      foot.hidden = true;
      return;
    }

    body.innerHTML = `${message ? `<p class="gk-mini-message" role="status">${escapeHtml(message)}</p>` : ''}<ul class="gk-mini-list">${cart.items.map(lineHtml).join('')}</ul>`;
    drawer.querySelector('[data-mini-subtotal]').textContent = formatMoney(cart.items_subtotal_price);
    foot.hidden = false;
  };

  const buildDrawer = () => {
    drawer = document.createElement('div');
    drawer.className = 'gk-mini';
    drawer.hidden = true;
    drawer.innerHTML = `
      <div class="gk-mini__overlay" data-mini-close></div>
      <aside class="gk-mini__panel" role="dialog" aria-modal="true" aria-label="Shopping bag">
        <header class="gk-mini__head">
          <h2>Shopping bag <small data-mini-count></small></h2>
          <button type="button" class="gk-icon-btn" data-mini-close aria-label="Close bag">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18"></path></svg>
          </button>
        </header>
        <div class="gk-mini__body" data-mini-body></div>
        <footer class="gk-mini__foot" data-mini-foot hidden>
          <div class="gk-mini__subtotal"><span>Subtotal</span><b data-mini-subtotal></b></div>
          <p>Shipping and taxes are calculated at checkout.</p>
          <a class="gk-cart-summary__checkout" href="/checkout">Checkout</a>
          <a class="gk-mini__view" href="${escapeHtml(config.cartUrl || '/cart')}">View bag</a>
        </footer>
      </aside>`;
    document.body.appendChild(drawer);

    drawer.addEventListener('click', async (event) => {
      if (event.target.closest('[data-mini-close]')) return close();
      const qty = event.target.closest('[data-mini-qty]');
      const remove = event.target.closest('[data-mini-remove]');
      if (!qty && !remove) return;
      const control = qty || remove;
      const quantity = qty ? Math.max(Number(qty.dataset.quantity) + Number(qty.dataset.miniQty), 0) : 0;
      drawer.classList.add('is-loading');
      try {
        const cart = await postJson('/cart/change.js', { line: Number(control.dataset.line), quantity });
        updateBadge(cart.item_count);
        renderDrawer(cart);
      } catch (e) {
        renderDrawer(await getCart(), e.message);
      }
      drawer.classList.remove('is-loading');
    });
  };

  const open = async (message) => {
    if (!drawer) buildDrawer();
    lastFocus = document.activeElement;
    drawer.hidden = false;
    requestAnimationFrame(() => drawer.classList.add('is-open'));
    document.documentElement.classList.add('gk-scroll-lock');
    const cart = await getCart();
    updateBadge(cart.item_count);
    renderDrawer(cart, typeof message === 'string' ? message : '');
    drawer.querySelector('.gk-mini__head .gk-icon-btn').focus();
  };

  const close = () => {
    if (!drawer || drawer.hidden) return;
    drawer.classList.remove('is-open');
    document.documentElement.classList.remove('gk-scroll-lock');
    setTimeout(() => {
      if (!drawer.classList.contains('is-open')) drawer.hidden = true;
    }, 320);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  };

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') close();
  });

  // The header bag opens the drawer everywhere except on the cart page itself.
  document.addEventListener('click', (event) => {
    const link = event.target.closest('.gk-actions__cart');
    if (!link || pageRoot() || event.metaKey || event.ctrlKey || event.shiftKey) return;
    event.preventDefault();
    open();
  });

  window.gillkartCart = { open, close, refresh: getCart, updateBadge, formatMoney };
})();
